import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import {
  BADGE_LOOKS,
  badgeGlyph,
  REPLIES_ANCHOR,
  aggregateReadLine,
  clampText,
  communityHref,
  communityProductHref,
  foundingTag,
  knownBadges,
  knownTier,
  memberHref,
  newReviewHref,
  relationLabel,
  replyCount,
  replyCountLine,
  replyPointsChip,
  reviewCountLine,
  reviewHref,
  safeImageUrl,
  sgScoreText,
  tierLabel,
  toPercentagesLine,
} from 'lib/community/model'
import type {
  CommunityFeedItem,
  CommunityPoints,
  CommunityProduct,
  CommunityProductStats,
  CommunityReviewResponse,
} from 'lib/community/types'
import type { ThreadEnrichment } from 'lib/community/threadEnrichment'
import { MaybeLink } from './Crumbs'
import MemberName from './MemberName'
import TierAvatar from './TierAvatar'
import VerifiedCheck from './VerifiedCheck'
import base from './community.module.css'
import badgeStyles from './Badges.module.css'
import styles from './Thread.module.css'
import enrich from './ThreadSideEnrichment.module.css'

/** Only an `https:` link to the SoundGuys review leaves the page: the field is DealHunt's. */
function safeReviewUrl(url: string | null | undefined): string | null {
  return url && /^https:\/\/[^\s]+$/.test(url) ? url : null
}

/**
 * The product strip over a thread (design 6): the image, the name (to the catalog product
 * page), the category and SoundGuys score, "Product page →". Every figure is DealHunt's; a
 * product without a slug is named without links.
 */
export const ProductStrip: React.VFC<{
  product: CommunityProduct
  stats: CommunityProductStats
  /** The deepest category the trail names ("Over-ear headphones"); falls back to the product's
   *  top-level one. */
  categoryName?: string | null
  /** The thread's enrichment (tagline, badge, specs, SoundGuys review); null/absent renders the
   *  strip as before. Not read yet (phase 2). */
  enrichment?: ThreadEnrichment | null
}> = ({ product, stats, categoryName = null }) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const image = safeImageUrl(product.image_url)
  const page = communityProductHref(product, trailingSlash)
  const category = categoryName ?? product.category?.name ?? null
  return (
    <div className={styles.strip}>
      <span className={styles.stripThumb}>
        {image && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={image} alt="" />
        )}
      </span>
      <div className={styles.stripText}>
        <MaybeLink href={page} className={styles.stripName}>
          {product.name}
        </MaybeLink>
        <span className={styles.stripFacts}>
          {category && <span>{category}</span>}
          {category && stats.sg_score !== null && <span aria-hidden="true">{' · '}</span>}
          {stats.sg_score !== null && (
            <span>
              {'SoundGuys score '}
              <b className={styles.stripScore}>{sgScoreText(stats.sg_score)}</b>
            </span>
          )}
        </span>
      </div>
      {page && (
        <Linked href={page} className={clsx(base.cardLink, styles.stripLink)}>
          {'Product page →'}
        </Linked>
      )}
    </div>
  )
}

/**
 * "Written by" (design 6): the author's public standing — tier, points, this month's rank,
 * the founding number and review count as pills, the badges earned. Every figure is DealHunt's.
 * An author who is not a member (not signed in, or profile hidden) has no standing and no
 * page: the card names the handle only.
 */
export const WrittenBy: React.VFC<{
  item: CommunityFeedItem
  standing: CommunityReviewResponse['author_standing']
  /** The summary's Early Owner cap (`founding.cap`) for the Early Owner hexagon; null = the
   *  digit-free mark (`author_standing` lists badge ids only, no line to read a cap from). */
  foundingCap?: number | null
}> = ({ item, standing, foundingCap = null }) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const { author } = item
  const tier = standing ? knownTier(standing.tier) : null
  const page = author.member ? memberHref(author.handle, trailingSlash) : null
  const badges = standing ? knownBadges(standing.badges_earned.map(id => ({ id }))) : []
  const founding = author.member?.founding_number
  const pills = [
    typeof founding === 'number' ? foundingTag(founding) : null,
    typeof standing?.reviews === 'number' && standing.reviews > 0
      ? reviewCountLine(standing.reviews)
      : null,
  ].filter((p): p is string => p !== null)
  return (
    <section className={base.card} aria-labelledby="written-by-title">
      <h2 id="written-by-title" className={base.eyebrow}>
        {'Written by'}
      </h2>
      <div className={styles.who}>
        <TierAvatar
          handle={author.handle}
          tier={tier}
          size="md"
          src={author.avatar_url}
          name={author.display_name}
        />
        <div className={styles.whoText}>
          {/* The check is a button: it sits beside the link, in the same inline run, never inside it. */}
          <span>
            <MaybeLink href={page} className={styles.whoName}>
              <MemberName name={author.display_name} handle={author.handle} />
            </MaybeLink>
            {author.member && <VerifiedCheck />}
          </span>
          <span className={styles.whoMeta}>
            {standing
              ? [
                  tierLabel(standing.tier),
                  `${standing.points.toLocaleString('en-US')} pts`,
                  standing.rank_month !== null ? `#${standing.rank_month} this month` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'Owner'}
          </span>
        </div>
      </div>
      {pills.length > 0 && (
        <ul className={styles.pills}>
          {pills.map(pill => (
            <li key={pill} className={styles.pill}>
              {pill}
            </li>
          ))}
        </ul>
      )}
      {badges.length > 0 && (
        <ul className={styles.badges} aria-label="Badges earned">
          {badges.map(badge => {
            const look = BADGE_LOOKS[badge.id]
            return (
              <li
                key={badge.id}
                className={clsx(badgeStyles.hex, badgeStyles[`badge_${look.tone}`], styles.hexSm)}
                title={look.name}
              >
                <span className={clsx(badgeStyles.hexFill, styles.hexSmFill)} aria-hidden="true">
                  {badgeGlyph(badge, foundingCap)}
                </span>
                <span className={styles.srOnly}>{look.name}</span>
              </li>
            )
          })}
        </ul>
      )}
      {page && (
        <Linked href={page} className={base.cardLink}>
          {'View profile →'}
        </Linked>
      )}
    </section>
  )
}

/** How many of the review's points the rail card lists: the PDP block has room for all. */
const RAIL_PROS = 3
const RAIL_CONS = 2

/**
 * "SoundGuys review" in the thread's rail: the PDP verdict block's facts, condensed — the score
 * (the "About this gear" score tile's look), the badge, one line of verdict (the product line's
 * tagline, else the review's verdict label), the first few Liked / Could be better points, the
 * byline and the one link to the review. Every field is SoundGuys enrichment or it is not
 * rendered: this card speaks for SoundGuys. Server-rendered, no state, no events.
 */
const RailReview: React.VFC<{
  review: NonNullable<ThreadEnrichment['review']>
  tagline: string | null
  badge: ThreadEnrichment['badge']
  url: string | null
}> = ({ review, tagline, badge, url }) => {
  const verdict = tagline ?? review.verdictLabel
  const badgeImage = badge ? safeImageUrl(badge.imageUrl) : null
  const pros = review.pros.slice(0, RAIL_PROS)
  const cons = review.cons.slice(0, RAIL_CONS)
  const reviewer = review.reviewers[0] ?? null
  const avatar = reviewer ? safeImageUrl(reviewer.avatarUrl) : null
  return (
    <section className={base.card} aria-labelledby="sg-review-title">
      <h2 id="sg-review-title" className={base.eyebrow}>
        {'SoundGuys review'}
      </h2>
      {(review.score !== null || badge) && (
        <div className={enrich.head}>
          {review.score !== null && (
            <div className={clsx(styles.tile, styles.tileScore, enrich.score)}>
              <b className={styles.tileBig}>
                {sgScoreText(review.score)}
                <small className={styles.tileOf}>{'/10'}</small>
              </b>
            </div>
          )}
          {badge && (
            <span className={enrich.badge}>
              {badgeImage && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={badgeImage} alt="" className={enrich.badgeImage} />
              )}
              <span className={enrich.badgeLabel}>{badge.label}</span>
            </span>
          )}
        </div>
      )}
      {verdict && <p className={enrich.verdict}>{verdict}</p>}
      {pros.length > 0 && (
        <div className={enrich.points}>
          <h3 className={clsx(enrich.pointsTitle, enrich.likeTitle)}>{'Liked'}</h3>
          <ul className={enrich.pointList}>
            {pros.map((point, i) => (
              <li key={`${i}-${point}`} className={enrich.point}>
                <span className={clsx(enrich.pointIcon, enrich.likeIcon)} aria-hidden="true">
                  {'✓'}
                </span>
                <span className={enrich.pointText}>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {cons.length > 0 && (
        <div className={enrich.points}>
          <h3 className={clsx(enrich.pointsTitle, enrich.dislikeTitle)}>{'Could be better'}</h3>
          <ul className={enrich.pointList}>
            {cons.map((point, i) => (
              <li key={`${i}-${point}`} className={enrich.point}>
                <span className={clsx(enrich.pointIcon, enrich.dislikeIcon)} aria-hidden="true">
                  {'✕'}
                </span>
                <span className={enrich.pointText}>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(reviewer || review.reviewDate) && (
        <div className={enrich.byline}>
          {reviewer &&
            (avatar ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={avatar} alt="" className={enrich.avatar} />
            ) : (
              <span className={enrich.avatar} aria-hidden="true">
                {reviewer.initials}
              </span>
            ))}
          <span className={enrich.bylineText}>
            {reviewer && <span className={enrich.bylineName}>{`By ${reviewer.name}`}</span>}
            {review.reviewDate && <span>{review.reviewDate}</span>}
          </span>
        </div>
      )}
      {url && (
        <a href={url} className={base.cardLink}>
          {'Read our review →'}
        </a>
      )}
    </section>
  )
}

/** "Key specs" in the thread's rail: DealHunt's compare specs, in its order, the unit already in
 *  the value (`ThreadEnrichment.specs`, at most `THREAD_SPECS_MAX`). Reviewed or not. */
const RailSpecs: React.VFC<{ specs: ThreadEnrichment['specs'] }> = ({ specs }) => (
  <section className={base.card} aria-labelledby="key-specs-title">
    <h2 id="key-specs-title" className={base.eyebrow}>
      {'Key specs'}
    </h2>
    <dl className={enrich.specs}>
      {specs.map(spec => (
        <div key={spec.key} className={enrich.spec}>
          <dt>{spec.label}</dt>
          <dd>{spec.value}</dd>
        </div>
      ))}
    </dl>
  </section>
)

/**
 * "About this gear" (design 6): two tiles — the SoundGuys score and the owner-review count —
 * then the owner figures the product page shows (buy-again share, each read as shares) once
 * DealHunt sends the aggregate (above the owner-stats gate), else how many more reviews the
 * gate needs; then "Read our review" when there is one. On a thread the product itself is the
 * strip's; the hub passes `product` and gets the catalog product-page link under the card.
 *
 * A thread with enrichment adds, as sibling cards in the rail, the SoundGuys review (when the
 * product line has one — "Read our review" then moves into it, so the link appears once) and
 * the key specs (when DealHunt sends any). No enrichment = today's card alone.
 */
export const AboutGear: React.VFC<{
  stats: CommunityProductStats
  product?: CommunityProduct
  /** The thread's enrichment (SoundGuys review, tagline, badge, specs); the hub passes none. */
  enrichment?: ThreadEnrichment | null
}> = ({ stats, product, enrichment = null }) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const productPage = product ? communityProductHref(product, trailingSlash) : null
  const legacyReview = safeReviewUrl(stats.sg_review_url)
  const enriched = enrichment?.review ?? null
  // The review card renders only when it has something of its own to show: a score, a point or
  // a reviewer. Without one, the card is skipped and this card keeps the link.
  const review =
    enriched &&
    (enriched.score !== null ||
      enriched.pros.length > 0 ||
      enriched.cons.length > 0 ||
      enriched.reviewers.length > 0)
      ? enriched
      : null
  const specs = enrichment?.specs ?? []
  const reviewUrl = review ? safeReviewUrl(review.reviewUrl) ?? legacyReview : null
  // With the review card, the card carries the link; the button here would repeat it.
  const sgReview = review ? null : legacyReview
  const toGo = toPercentagesLine(stats.to_percentages)
  const aggregate = stats.aggregate ?? null
  const reads = aggregate
    ? aggregate.reads
        .map(read => ({ id: read.id, label: read.label, line: aggregateReadLine(read) }))
        .filter(read => read.line)
    : []
  const card = (
    <section className={base.card} aria-labelledby="about-gear-title">
      <h2 id="about-gear-title" className={base.eyebrow}>
        {'About this gear'}
      </h2>
      <div className={styles.tiles}>
        {/* With a scored review card the score is its; the tile here would repeat it. */}
        {(review?.score ?? null) === null && stats.sg_score !== null && (
          <div className={clsx(styles.tile, styles.tileScore)}>
            <b className={styles.tileBig}>
              {sgScoreText(stats.sg_score)}
              <small className={styles.tileOf}>{'/10'}</small>
            </b>
            <span className={styles.tileLabel}>{'SoundGuys score'}</span>
          </div>
        )}
        <div className={styles.tile}>
          <b className={styles.tileBig}>{stats.published_count.toLocaleString('en-US')}</b>
          <span className={styles.tileLabel}>
            {stats.published_count === 1 ? 'owner review' : 'owner reviews'}
          </span>
        </div>
      </div>
      {aggregate && (aggregate.buy_again_pct !== null || reads.length > 0) ? (
        <dl className={styles.figures}>
          {aggregate.buy_again_pct !== null && (
            <div className={styles.figure}>
              <dt>{'Would buy again'}</dt>
              <dd>{`${Math.round(aggregate.buy_again_pct)}%`}</dd>
            </div>
          )}
          {reads.map(read => (
            <div key={read.id} className={styles.figure}>
              <dt>{read.label}</dt>
              <dd>{read.line}</dd>
            </div>
          ))}
        </dl>
      ) : (
        toGo && <p className={base.text}>{toGo}</p>
      )}
      {sgReview && (
        <a href={sgReview} className={base.btn}>
          {'Read our review'}
        </a>
      )}
      {productPage && (
        <Linked href={productPage} className={base.cardLink}>
          {'See the product page →'}
        </Linked>
      )}
    </section>
  )
  if (!review && !specs.length) {
    return card
  }
  return (
    <>
      {card}
      {review && (
        <RailReview
          review={review}
          tagline={enrichment?.tagline ?? null}
          badge={enrichment?.badge ?? null}
          url={reviewUrl}
        />
      )}
      {specs.length > 0 && <RailSpecs specs={specs} />}
    </>
  )
}

/**
 * "More owner reviews" under a thread (design 6): this product's other reviews, then other
 * owners on similar gear, each with its thumb, why it is here (same product / same brand /
 * similar gear) and the way in — "Reply first +10" while nobody has replied, the reply count
 * once someone has. The sentence is shown (clamped), so each row carries `data-note-id` under
 * the page's views beacon. "All N →" is the community feed; N is DealHunt's total.
 */
export const MoreReviews: React.VFC<{
  product: CommunityProduct
  items: CommunityFeedItem[]
  total: number | null
  points: CommunityPoints | null
}> = ({ product, items, total, points }) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const pts = replyPointsChip(points)
  if (!items.length) {
    return null
  }
  return (
    <section className={styles.more} aria-labelledby="more-reviews-title">
      <div className={styles.moreHead}>
        <h2 id="more-reviews-title" className={styles.moreTitle}>
          {'More owner reviews'}
        </h2>
        {total !== null && total > items.length && (
          <Linked href={communityHref(trailingSlash)} className={base.cardLink}>
            {`All ${total.toLocaleString('en-US')} →`}
          </Linked>
        )}
      </div>
      <ul className={styles.moreList}>
        {items.map(item => {
          const image = safeImageUrl(item.product.image_url)
          const thread = reviewHref(item, trailingSlash)
          const replies = replyCount(item)
          return (
            <li key={item.note.id} className={styles.moreRow} data-note-id={item.note.id}>
              <MaybeLink href={thread} className={styles.moreThumb}>
                {image && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={image} alt="" loading="lazy" />
                )}
              </MaybeLink>
              <div className={styles.moreBody}>
                <span className={styles.moreMeta}>
                  <b>{item.product.name}</b>
                  {` · ${item.author.handle}`}
                </span>
                <MaybeLink href={thread} className={styles.moreQuote}>
                  {`“${clampText(item.note.note, 180)}”`}
                </MaybeLink>
                <span className={styles.moreFoot}>
                  <span>{relationLabel(item.product, product)}</span>
                  {replies === 0 && thread && (
                    <>
                      <span aria-hidden="true">{' · '}</span>
                      <Linked href={`${thread}#${REPLIES_ANCHOR}`} className={styles.replyFirst}>
                        {'Reply first'}
                        {pts && <span className={styles.ptsMini}>{pts.replace(' pts', '')}</span>}
                      </Linked>
                    </>
                  )}
                  {replies !== null && replies > 0 && thread && (
                    <>
                      <span aria-hidden="true">{' · '}</span>
                      <Linked href={`${thread}#${REPLIES_ANCHOR}`} className={styles.moreReplies}>
                        {replyCountLine(replies)}
                      </Linked>
                    </>
                  )}
                </span>
              </div>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/**
 * "Own the <product> too?" — to the community review form for this product. `points` is what a
 * review there earns (DealHunt's figure; null when the page has no points table — the card then
 * promises no number). No points for replying: DealHunt does not see commentHub yet.
 */
export const OwnItToo: React.VFC<{
  product: CommunityProduct
  points: number | null
  bonus: boolean
}> = ({ product, points, bonus }) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  if (!product.slug) {
    return null
  }
  return (
    <section className={clsx(base.card, base.cardInfo)} aria-labelledby="own-too-title">
      <h2 id="own-too-title" className={styles.ownTitle}>
        {`Own the ${product.name} too?`}
      </h2>
      <p className={base.text}>
        {bonus
          ? 'Nobody has reviewed it yet: the first owner review earns a bonus.'
          : 'Agree or disagree with this owner. Every review helps the next buyer.'}
      </p>
      <Linked href={newReviewHref(product.slug, trailingSlash)} className={base.btn}>
        {points !== null ? `Write an owner review (+${points})` : 'Write an owner review'}
      </Linked>
    </section>
  )
}
